import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEventSubCatalog, useReconcile, useSaveSubscriptions, useSubscriptions } from '../../api/hooks';
import { buildDottedTree } from '../../domain/tree';
import { runtimeLabel } from '../../domain/labels';
import { Button, ErrorNote, Loading, Section, StatePill, TreeSelect } from '../../ui';

export function SubscriptionsPage(): JSX.Element {
  const { botId = '', channelId = '' } = useParams();
  const catalog = useEventSubCatalog();
  const subs = useSubscriptions(botId, channelId);
  const save = useSaveSubscriptions(botId, channelId);
  const reconcile = useReconcile();
  const [draft, setDraft] = useState<Set<string> | null>(null);

  const nodes = useMemo(
    () => buildDottedTree((catalog.data ?? []).map((t) => ({ type: t.type, version: t.version }))),
    [catalog.data],
  );
  const selected = useMemo(() => {
    if (draft) return draft;
    return new Set((subs.data?.plan ?? []).filter((p) => p.desired).map((p) => `${p.type}@${p.version}`));
  }, [draft, subs.data]);

  if (catalog.isPending || subs.isPending) return <Loading />;
  if (subs.isError) return <ErrorNote>{subs.error.message}</ErrorNote>;
  const plan = subs.data?.plan ?? [];
  const dirty = Boolean(draft);

  return (
    <Section
      title="Plan EventSub"
      actions={
        <>
          <Button
            onClick={() => reconcile.mutate({ botId, buid: channelId })}
            loading={reconcile.isPending}
          >
            Réconcilier
          </Button>
          <Button
            variant="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => {
              const types = [...selected].map((key) => {
                const [type, version] = key.split('@');
                return { type: type ?? key, version: version ?? '1' };
              });
              save.mutate(types, { onSuccess: () => setDraft(null) });
            }}
          >
            Appliquer
          </Button>
        </>
      }
    >
      <TreeSelect nodes={nodes} selected={selected} onChange={setDraft} />
      <div style={{ marginTop: 'var(--tbm-space-4)' }}>
        {plan.filter((p) => p.desired).map((p) => (
          <div key={`${p.type}@${p.version}`} className="tbm-toolbar">
            <code className="tbm-id">{p.type}</code>
            <StatePill state={p.runtime?.status ?? 'pending'}>{runtimeLabel(p.runtime?.status)}</StatePill>
            {p.runtime?.lastError ? <span className="tbm-scope--missing">{p.runtime.lastError}</span> : null}
            {p.runtime?.missingScopes?.length ? <span className="tbm-scope--missing">{p.runtime.missingScopes.join(', ')}</span> : null}
          </div>
        ))}
      </div>
    </Section>
  );
}
